/**
 * web-vitals adapter — wires the standard CWV metrics into our reporter.
 *
 * Dynamic-imported so the ~3 KB web-vitals bundle stays out of the initial
 * critical path; startWebVitalsCollection() is called after the React app
 * paints in main.tsx.
 */

import { reportPerformance } from "./reporter.js";
import type { PerfMetric } from "./types.js";

export async function startWebVitalsCollection(): Promise<void> {
  if (typeof window === "undefined") return;
  // Skip in tests — jsdom doesn't implement most of the observers web-vitals
  // depends on, and we don't want stray reports polluting unrelated specs.
  if (typeof navigator !== "undefined" && navigator.userAgent.includes("jsdom")) return;
  try {
    const mod = await import("web-vitals");
    const dispatch = (metric: {
      name: string;
      value: number;
      rating?: string;
      id?: string;
      navigationType?: string;
    }) => {
      const next: PerfMetric = {
        name: metric.name,
        value: metric.value,
        kind: "web-vital",
        rating: metric.rating,
        id: metric.id,
        meta: metric.navigationType ? { navigationType: metric.navigationType } : undefined,
      };
      reportPerformance(next);
    };
    mod.onLCP(dispatch);
    mod.onINP(dispatch);
    mod.onCLS(dispatch);
    mod.onFCP(dispatch);
    mod.onTTFB(dispatch);
  } catch (err) {
    console.warn("[tree-hole perf] web-vitals load failed", err);
  }
}
